import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Card, CardBody, CardHeader, Row, Col, Table, Input, Button } from "reactstrap";
import axios from "../../config/axios";
import { toast } from "react-toastify";

const AdminAnalyticsDashboard = () => {
  const { token: authToken } = useSelector((state) => state.auth);
  const [datePreset, setDatePreset] = useState("last_7d");
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);

  const formatChannelLabel = (value) => {
    const map = {
      paid_social: "Paid Social",
      organic_social: "Organic Social",
      paid_search: "Paid Search",
      organic_search: "Organic Search",
      email: "Email",
      referral: "Referral",
      direct: "Direct",
      other: "Other",
    };
    return map[value] || value || "Other";
  };
  const formatSourceLabel = (value) => {
    if (!value) return "direct";
    return String(value)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());
  };
  const percent = (part, total) => {
    if (!total) return "0.00%";
    return `${((Number(part || 0) / Number(total || 0)) * 100).toFixed(2)}%`;
  };

  const loadOverview = async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `/admin/analytics/overview?date_preset=${encodeURIComponent(datePreset)}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setOverview(response?.data?.data || null);
    } catch (err) {
      setOverview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(
      "Are you sure you want to reset all traffic analytics data? This action cannot be undone and will only clear tracking data (page views, visitors, etc.), not orders or users."
    )) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete("/admin/analytics/reset", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success("Traffic analytics data reset successfully!");
      await loadOverview();
    } catch (err) {
      toast.error("Failed to reset analytics data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [authToken, datePreset]);

  return (
    <div className="animated fadeIn" style={{ marginTop: "20px" }}>
      <Row>
        <Col xs="12">
          <Card className="mb-3">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <b>Admin Analytics Dashboard</b>
              <div className="d-flex align-items-center">
                <Input
                  type="select"
                  value={datePreset}
                  onChange={(e) => setDatePreset(e.target.value)}
                  style={{ width: 180 }}
                  className="mr-2"
                >
                  <option value="last_7d">Last 7 days</option>
                  <option value="last_14d">Last 14 days</option>
                  <option value="last_30d">Last 30 days</option>
                </Input>
                <Button size="sm" color="secondary" onClick={loadOverview} disabled={loading}>
                  {loading ? "Refreshing..." : "Refresh"}
                </Button>
                <Button size="sm" color="danger" onClick={handleReset} disabled={loading} className="ml-2">
                  Reset Traffic
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div style={{ textAlign: "center" }}>
                  <i className="fas fa-spinner fa-pulse fa-2x" style={{ color: "green" }}></i>
                </div>
              ) : overview ? (
                <>
                  <Row className="mb-3">
                    <Col md="2"><Card body><b>Users</b><div>{Number(overview?.platform?.totalUsers || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Sellers</b><div>{Number(overview?.platform?.totalSellers || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Buyers</b><div>{Number(overview?.platform?.totalBuyers || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Sales</b><div>${Number(overview?.commerce?.grossSales || 0).toFixed(2)}</div></Card></Col>
                    <Col md="2"><Card body><b>Orders</b><div>{Number(overview?.commerce?.totalOrders || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Unique Buyers</b><div>{Number(overview?.commerce?.uniqueBuyers || 0).toLocaleString()}</div></Card></Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md="2"><Card body><b>Products</b><div>{Number(overview?.catalog?.totalProducts || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Digital</b><div>{Number(overview?.catalog?.digitalProducts || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Physical</b><div>{Number(overview?.catalog?.physicalProducts || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Meta Impressions</b><div>{Number(overview?.marketing?.meta?.impressions || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Meta Clicks</b><div>{Number(overview?.marketing?.meta?.clicks || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Meta Spend</b><div>${Number(overview?.marketing?.meta?.spend || 0).toFixed(2)}</div></Card></Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md="2"><Card body><b>Page Views</b><div>{Number(overview?.traffic?.pageViews || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Visitors</b><div>{Number(overview?.traffic?.uniqueVisitors || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>New Visitors</b><div>{Number(overview?.traffic?.newVisitors || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Returning</b><div>{Number(overview?.traffic?.returningVisitors || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Sessions</b><div>{Number(overview?.traffic?.sessions || 0).toLocaleString()}</div></Card></Col>
                    <Col md="2"><Card body><b>Bounce Rate</b><div>{Number(overview?.traffic?.bounceRate || 0).toFixed(2)}%</div></Card></Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md="6">
                      <Card body>
                        <b>Top Sellers (period)</b>
                        <Table bordered striped responsive size="sm" className="mt-2 mb-0">
                          <thead>
                            <tr><th>Seller</th><th>Email</th><th>Orders</th><th>Sales</th></tr>
                          </thead>
                          <tbody>
                            {(overview?.topSellers || []).length > 0 ? (
                              overview.topSellers.map((item, index) => (
                                <tr key={`${item.sellerId}-${index}`}>
                                  <td>{(item.sellerName || "").trim() || "Unknown Seller"}</td>
                                  <td>{item.sellerEmail || "-"}</td>
                                  <td>{Number(item.totalOrders || 0).toLocaleString()}</td>
                                  <td>${Number(item.grossSales || 0).toFixed(2)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="4" className="text-center text-muted">No seller data in this period.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </Card>
                    </Col>
                    <Col md="6">
                      <Card body>
                        <b>Top Products (period)</b>
                        <Table bordered striped responsive size="sm" className="mt-2 mb-0">
                          <thead>
                            <tr><th>Product</th><th>Type</th><th>Qty</th><th>Revenue</th></tr>
                          </thead>
                          <tbody>
                            {(overview?.topProducts || []).length > 0 ? (
                              overview.topProducts.map((item) => (
                                <tr key={item._id}>
                                  <td>{item.name}</td>
                                  <td>{item.productType === "digital" ? `digital${item.digitalType ? ` (${item.digitalType})` : ""}` : "physical"}</td>
                                  <td>{Number(item.qtySold || 0).toLocaleString()}</td>
                                  <td>${Number(item.revenue || 0).toFixed(2)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="4" className="text-center text-muted">No product data in this period.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md="4">
                      <Card body>
                        <b>Top traffic sources</b>
                        <Table bordered striped responsive size="sm" className="mt-2 mb-0">
                          <thead><tr><th>Source</th><th>Views</th><th>Share</th></tr></thead>
                          <tbody>
                            {(overview?.traffic?.topSources || []).length > 0 ? (
                              overview.traffic.topSources.map((item, index) => (
                                <tr key={`${item._id}-${index}`}>
                                  <td>{formatSourceLabel(item._id || "direct")}</td>
                                  <td>{Number(item.views || 0).toLocaleString()}</td>
                                  <td>{percent(item.views, overview?.traffic?.pageViews)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="3" className="text-center text-muted">No tracked data yet.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </Card>
                    </Col>
                    <Col md="4">
                      <Card body>
                        <b>Channel breakdown</b>
                        <Table bordered striped responsive size="sm" className="mt-2 mb-0">
                          <thead><tr><th>Channel</th><th>Views</th><th>Share</th></tr></thead>
                          <tbody>
                            {(overview?.traffic?.channelBreakdown || []).length > 0 ? (
                              overview.traffic.channelBreakdown.map((item, index) => (
                                <tr key={`${item._id}-${index}`}>
                                  <td>{formatChannelLabel(item._id || "other")}</td>
                                  <td>{Number(item.views || 0).toLocaleString()}</td>
                                  <td>{percent(item.views, overview?.traffic?.pageViews)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="3" className="text-center text-muted">No tracked data yet.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </Card>
                    </Col>
                    <Col md="4">
                      <Card body>
                        <b>Country breakdown</b>
                        <Table bordered striped responsive size="sm" className="mt-2 mb-0">
                          <thead><tr><th>Country</th><th>Views</th><th>Share</th></tr></thead>
                          <tbody>
                            {(overview?.traffic?.countryBreakdown || []).length > 0 ? (
                              overview.traffic.countryBreakdown.map((item, index) => (
                                <tr key={`${item._id}-${index}`}>
                                  <td>{item._id || "unknown"}</td>
                                  <td>{Number(item.views || 0).toLocaleString()}</td>
                                  <td>{percent(item.views, overview?.traffic?.pageViews)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="3" className="text-center text-muted">No tracked data yet.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </Card>
                    </Col>
                  </Row>
                </>
              ) : (
                <div className="text-muted">Unable to load admin analytics right now.</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminAnalyticsDashboard;
